import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { BasePage, PageType } from 'src/components/BasePage';
import { handleStartProcessCustomEvent } from 'src/components/EntityPresentation/utils/handleStartProcessCustomEvent';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';

type EditPageProps = {
    pageName: string;
    store: BasicStore<Entity>;
    baseUrl: string;
    cancelUrl?: string;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveUrl?: string | ((id: string | number) => string);
};

export const EditPage = observer(
    ({
        pageName,
        store,
        baseUrl,
        cancelUrl = baseUrl,
        saveUrl = baseUrl,
    }: EditPageProps) => {
        const {
            params: { id },
        } = useRouteMatch<{ id: string }>();

        useEffect(() => {
            store.loadForm(store.itemEditFormKey);
            store.loadItem(id);

            return () => {
                store.formLoader.reset();
                store.itemLoader.reset();
            };
        }, []);

        const navigateAfterSave = () => {
            const to = typeof saveUrl === 'function' ? saveUrl(id) : saveUrl;
            Router.navigate(to);
        };

        const loadingStatus = useMemo(() => {
            return new CombinedLoadingStatus([
                store.formLoader,
                store.itemLoader,
            ]);
        }, []);

        if (loadingStatus.isLoading) {
            return <SpinnerFullWidth />;
        }

        return (
            <BasePage pageName={`${pageName}-${id}`} type={PageType.FULL}>
                <FormioForm
                    form={store.currentItemForm!}
                    submission={{ data: store.currentItem }}
                    onCustomEvent={async (event) => {
                        if (event.type === 'startProcess') {
                            handleStartProcessCustomEvent(event, baseUrl);
                        } else if (event.type === 'cancel') {
                            Router.navigate(cancelUrl);
                        } else if (event.type === 'save') {
                            await store.update(id, event.data);
                            navigateAfterSave();
                        }

                        return Promise.resolve();
                    }}
                    onSubmit={async (submission) => {
                        await store.update(id, submission.data!);
                        await store.loadList();
                        navigateAfterSave();
                    }}
                />
            </BasePage>
        );
    },
);
